import { Component, effect, Inject, input, output, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import 'moment/locale/fr';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';

import { Promotions } from 'src/app/shared/interfaces/promotions/promotions';
import { FilterPromotionsRequest } from '../../shared/interfaces/promotions/filterPromotionsRequest';

const FILTER_PROMOTIONS_REQUEST_STRING = 'FilterPromotionsRequest';
@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatButtonModule,
    MatCard,
    MatCardContent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatOption,
    MatSelect,
    MatTableModule,
  ],
  providers: [provideMomentDateAdapter()],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss'
})
export class FiltersComponent implements OnInit {

  dataSource = input<MatTableDataSource<Promotions>>();
  statutList: string[] = [];
  modeCompensationList: string[] = [];

  filterChange = output<Promotions[]>();

  selectionFilter: string = '';
  statutFilter: string = '';
  modeCompensationFilter: string = '';

  period = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null)
  });
  stringFilter: string = '';

  constructor(
    private _adapter: DateAdapter<any>,
    @Inject(MAT_DATE_LOCALE) private _locale: string
  ) {
    effect(() => {
      this.statutList = [...new Set(this.dataSource()?.data.map((promotion) => promotion.statut).filter((statut): statut is string => statut !== null && statut !== undefined))];
      this.modeCompensationList = [...new Set(this.dataSource()?.data.map((promotion) => promotion.modeCompensation).filter((modeCompensation): modeCompensation is string => modeCompensation !== null && modeCompensation !== undefined))];

      this.setFilteredData();
      if (this.dataSource()) {
        this.filterChange.emit(this.dataSource()!.filteredData);
      }
    });
  }
  ngOnInit(): void {
    this._locale = 'fr';// local for calendar
    this._adapter.setLocale(this._locale);
    if (!this.getFilterInSession()) {
      sessionStorage.setItem(FILTER_PROMOTIONS_REQUEST_STRING, JSON.stringify({ stringFilter:"", isSelected: "true", statut: 'En Cours', modeCompensation: '', period: { start: null, end: null } }));
    }

    this.applyRequestFilter(JSON.parse(this.getFilterInSession()));
  }


  applyRequestFilter(filter: FilterPromotionsRequest) {
    this.stringFilter = this.getFilterInSession() ? this.getObjectFilterInSession().stringFilter : '';
    this.selectionFilter = filter.isSelected === null ? '' : filter.isSelected!.toString();
    this.statutFilter = filter.statut ?? '';
    this.modeCompensationFilter = filter.modeCompensation ?? '';
    this.period.setValue({ start: this.getObjectFilterInSession().period.start ? new Date(this.getObjectFilterInSession().period.start) : null, end: this.getObjectFilterInSession().period.end ? new Date(this.getObjectFilterInSession().period.end) : null });
    this.setFilteredData();
  }

  clearFilters() {
    this.stringFilter = "";
    this.selectionFilter = "";
    this.statutFilter = "";
    this.period.setValue({ start: null, end: null });
    this.modeCompensationFilter = "";
    this.setFilteredData();
  }


  onStringFilter(filter: string) {
    this.stringFilter = filter;
    this.setFilteredData();
  }

  onSelectChange(value: string) {
    this.selectionFilter = value;
    this.setFilteredData();
  }

  onStatutChange(value: string) {
    this.statutFilter = value;
    this.setFilteredData();
  }

  onPeriodeChange() {
    this.setFilteredData();
  }

  onModeCompensationChange(value: string) {
    this.modeCompensationFilter = value;
    this.setFilteredData();
  }

  private setFilteredData() {
    if (this.dataSource() && this.dataSource()!.data.length > 0) {
      this.setFilterPredicate();
      this.dataSource()!.filter = "needsNoneNullValue";// if we don't set a value, the filter won't work
      this.filterChange.emit(this.dataSource()!.filteredData);
    }
  }

  isFiltered(): boolean {
    if (this.stringFilter !== '' ||
      this.selectionFilter !== '' ||
      this.statutFilter !== '' ||
      (this.period.value['start'] !== null && this.period.value['end'] !== null) ||
      this.modeCompensationFilter !== '') {
      return true;
    }
    return false;
  }

  setFilterPredicate() {
    this.dataSource()!.filterPredicate = (data): boolean => {
      return this.isStringFilterMatch(data) &&
        this.isSelectionFilterMatch(data) &&
        this.isStatutFilterMatch(data) &&
        this.isPeriodFilterMatch(data) &&
        this.isModeCompensationFilterMatch(data);
    };
  }

  private isStringFilterMatch(data: Promotions): boolean {
    sessionStorage.setItem(FILTER_PROMOTIONS_REQUEST_STRING, JSON.stringify({ ...this.getObjectFilterInSession(), stringFilter: this.stringFilter }));
    if (this.stringFilter) {
      return (data.libellePromo?.toLowerCase().includes(this.stringFilter.toLowerCase()) ?? false) ||
        (data.nomLaboratoire?.toLowerCase().includes(this.stringFilter.toLowerCase()) ?? false);
    }
    return true;
  }

  private isSelectionFilterMatch(data: Promotions): boolean {
    sessionStorage.setItem(FILTER_PROMOTIONS_REQUEST_STRING, JSON.stringify({ ...this.getObjectFilterInSession(), isSelected: this.selectionFilter }));
    if (this.selectionFilter === 'true') {
      return data.flagSelection;
    }
    if (this.selectionFilter === 'false') {
      return !data.flagSelection;
    }
    return true;
  }

  private isStatutFilterMatch(data: Promotions): boolean {
    sessionStorage.setItem(FILTER_PROMOTIONS_REQUEST_STRING, JSON.stringify({ ...this.getObjectFilterInSession(), statut: this.statutFilter }));
    if (this.statutFilter) {
      return data.statut?.toLowerCase().includes(this.statutFilter.toLowerCase()) ?? false;
    }
    return true;
  }

  private isPeriodFilterMatch(data: Promotions): boolean {
    sessionStorage.setItem(FILTER_PROMOTIONS_REQUEST_STRING, JSON.stringify({ ...this.getObjectFilterInSession(), period: this.period.value }));
    if (this.period.value['start'] !== null && this.period.value['end'] !== null) {
      return (new Date(data.dateDebut) >= this.period.value['start']! && new Date(data.dateDebut) <= this.period.value['end']!) ||
        (new Date(data.dateFin) >= this.period.value['start']! && new Date(data.dateFin) <= this.period.value['end']!) ||
        (new Date(data.dateDebut) <= this.period.value['start']! && new Date(data.dateFin) >= this.period.value['end']!);
    }
    return true;
  }

  private isModeCompensationFilterMatch(data: Promotions): boolean {
    sessionStorage.setItem(FILTER_PROMOTIONS_REQUEST_STRING, JSON.stringify({ ...this.getObjectFilterInSession(), modeCompensation: this.modeCompensationFilter }));
    if (this.modeCompensationFilter) {
      return data.modeCompensation?.toLowerCase().includes(this.modeCompensationFilter.toLowerCase()) ?? false;
    }
    return true;
  }

  //#region SessionStorage
  private getFilterInSession() {
    if (sessionStorage.getItem(FILTER_PROMOTIONS_REQUEST_STRING)) {
      return sessionStorage.getItem(FILTER_PROMOTIONS_REQUEST_STRING)!;
    }
    return "";
  }

  private getObjectFilterInSession(): any {
    return JSON.parse(this.getFilterInSession());
  }
  //#endregion
}
